/**
 * Command Palette Component (Cmd+K / Ctrl+K)
 * Quick navigation and action execution interface
 */

import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  ViewChild,
  ElementRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { AppKnowledgeService } from '../core/app-knowledge.service';
import { ActionExecutorService } from '../core/action-executor.service';
import { IntentClassifierService } from '../core/intent-classifier.service';
import { QuickActionsService, QuickAction } from '../core/quick-actions.service';
import { DialogService, DialogType } from '../../shared/dialog/dialog.service';
import {
  ActionDefinition,
  RouteDefinition
} from '../models/app-knowledge.models';

interface CommandPaletteItem {
  type: 'route' | 'action' | 'recent' | 'quick-action';
  id: string;
  title: string;
  subtitle?: string;
  icon?: string;
  category?: string;
  data?: RouteDefinition | ActionDefinition | QuickAction;
}

@Component({
  selector: 'ai-command-palette',
  templateUrl: './command-palette.component.html',
  styleUrls: ['./command-palette.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule]
})
export class CommandPaletteComponent implements OnInit, OnDestroy {

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  isOpen = false;
  query = '';
  items: CommandPaletteItem[] = [];
  selectedIndex = 0;

  private querySubject = new Subject<string>();
  private recentItems: CommandPaletteItem[] = [];
  private readonly MAX_RECENT = 5;

  constructor(
    private router: Router,
    private knowledgeService: AppKnowledgeService,
    private actionExecutor: ActionExecutorService,
    private intentClassifier: IntentClassifierService,
    private quickActionsService: QuickActionsService,
    private dialogService: DialogService
  ) {}

  ngOnInit(): void {
    // Debounce search queries
    this.querySubject
      .pipe(
        debounceTime(150),
        distinctUntilChanged()
      )
      .subscribe(query => {
        this.performSearch(query);
      });

    // Load recent items from localStorage
    this.loadRecentItems();

    // Show all items initially
    this.showAllItems();
  }

  ngOnDestroy(): void {
    this.querySubject.complete();
  }

  // ============================================================
  // KEYBOARD SHORTCUTS
  // ============================================================

  @HostListener('document:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    // Cmd+K or Ctrl+K to toggle
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.toggle();
      return;
    }

    // Only handle other keys when open
    if (!this.isOpen) return;

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        break;

      case 'ArrowDown':
        event.preventDefault();
        this.selectNext();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.selectPrevious();
        break;

      case 'Enter':
        event.preventDefault();
        this.executeSelected();
        break;

      case 'Tab':
        event.preventDefault();
        if (event.shiftKey) {
          this.selectPrevious();
        } else {
          this.selectNext();
        }
        break;
    }
  }

  // ============================================================
  // OPEN/CLOSE
  // ============================================================

  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    this.isOpen = true;
    this.query = '';
    this.selectedIndex = 0;
    this.showAllItems();

    // Focus input after render
    setTimeout(() => {
      this.searchInput?.nativeElement?.focus();
    }, 50);
  }

  close(): void {
    this.isOpen = false;
    this.query = '';
    this.items = [];
  }

  // ============================================================
  // SEARCH
  // ============================================================

  onQueryChange(query: string): void {
    this.query = query;
    this.selectedIndex = 0;
    this.querySubject.next(query);
  }

  private async performSearch(query: string): Promise<void> {
    if (!query.trim()) {
      this.showAllItems();
      return;
    }

    const queryLower = query.toLowerCase();
    const items: CommandPaletteItem[] = [];

    // Search quick actions (Speed Dial FAB actions)
    const quickActions = this.quickActionsService.searchActions(query);
    for (const quickAction of quickActions.slice(0, 5)) {
      items.push({
        type: 'quick-action',
        id: quickAction.id,
        title: quickAction.text,
        subtitle: quickAction.tooltip,
        icon: this.quickActionsService.getEmojiForIcon(quickAction.icon),
        category: 'Quick Action',
        data: quickAction
      });
    }

    // Search routes
    const routes = this.knowledgeService.searchRoutes(query);
    for (const route of routes.slice(0, 5)) {
      items.push({
        type: 'route',
        id: route.path,
        title: route.displayName,
        subtitle: route.description,
        icon: this.getIconForRoute(route.path),
        category: 'Navigate',
        data: route
      });
    }

    // Search actions
    const actions = this.knowledgeService.searchActions(query);
    for (const action of actions.slice(0, 5)) {
      items.push({
        type: 'action',
        id: action.id,
        title: action.displayName,
        subtitle: action.description,
        icon: this.getIconForCategory(action.category),
        category: this.capitalizeCategory(action.category),
        data: action
      });
    }

    // Show recent if few results
    if (items.length < 3) {
      items.push(...this.recentItems.slice(0, 3));
    }

    this.items = items;
  }

  private showAllItems(): void {
    const items: CommandPaletteItem[] = [];

    // Add recent items first
    if (this.recentItems.length > 0) {
      items.push(...this.recentItems);
    }

    // Add quick actions (Speed Dial FAB actions) - Top priority!
    const quickActions = this.quickActionsService.getActions();
    for (const quickAction of quickActions.slice(0, 6)) {
      items.push({
        type: 'quick-action',
        id: quickAction.id,
        title: quickAction.text,
        subtitle: quickAction.tooltip,
        icon: this.quickActionsService.getEmojiForIcon(quickAction.icon),
        category: 'Quick Action',
        data: quickAction
      });
    }

    // Get all routes
    const knowledge = this.knowledgeService.getKnowledgeValue();
    if (knowledge) {
      const routes = Object.values(knowledge.routes) as RouteDefinition[];
      for (const route of routes.slice(0, 6)) {
        items.push({
          type: 'route',
          id: route.path,
          title: route.displayName,
          subtitle: route.description,
          icon: this.getIconForRoute(route.path),
          category: 'Navigate',
          data: route
        });
      }

      // Get common actions
      const commonActions = this.getCommonActions();
      for (const action of commonActions.slice(0, 4)) {
        items.push({
          type: 'action',
          id: action.id,
          title: action.displayName,
          subtitle: action.description,
          icon: this.getIconForCategory(action.category),
          category: this.capitalizeCategory(action.category),
          data: action
        });
      }
    }

    this.items = items;
  }

  // ============================================================
  // SELECTION
  // ============================================================

  selectNext(): void {
    if (this.items.length === 0) return;
    this.selectedIndex = (this.selectedIndex + 1) % this.items.length;
    this.scrollToSelected();
  }

  selectPrevious(): void {
    if (this.items.length === 0) return;
    this.selectedIndex = this.selectedIndex === 0
      ? this.items.length - 1
      : this.selectedIndex - 1;
    this.scrollToSelected();
  }

  selectItem(index: number): void {
    this.selectedIndex = index;
  }

  private scrollToSelected(): void {
    setTimeout(() => {
      const element = document.querySelector('.palette-item.selected');
      if (element) {
        element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }, 10);
  }

  // ============================================================
  // EXECUTION
  // ============================================================

  async executeSelected(): Promise<void> {
    if (this.items.length === 0) return;

    const item = this.items[this.selectedIndex];
    await this.executeItem(item);
  }

  async executeItem(item: CommandPaletteItem): Promise<void> {
    // Add to recent items
    this.addToRecent(item);

    // Execute based on type
    try {
      if (item.type === 'route') {
        await this.router.navigateByUrl(item.id);
      } else if (item.type === 'action') {
        const action = item.data as ActionDefinition;
        await this.actionExecutor.executeActionDefinition(
          action,
          {},
          { mode: 'navigate' }
        );
      } else if (item.type === 'quick-action') {
        const quickAction = item.data as QuickAction;

        if (quickAction.component) {
          this.openQuickActionComponent(quickAction);
        } else if (quickAction.route) {
          await this.router.navigateByUrl(quickAction.route);
        } else if (quickAction.rootRoute) {
          await this.router.navigateByUrl(quickAction.rootRoute);
        }
      }

      this.close();
    } catch (error) {
      console.error('Failed to execute command palette item:', error);
    }
  }

  private openQuickActionComponent(action: QuickAction): void {
    if (!action.component) {
      return;
    }

    const dialogType = action.dialogType ?? DialogType.SIDE_MODAL;
    const closeOnClickOutside = action.closeOnClickOutside ?? true;
    this.dialogService.open(
      action.component,
      action.dialogConfig ?? {},
      dialogType,
      closeOnClickOutside
    );
  }

  // ============================================================
  // RECENT ITEMS
  // ============================================================

  private addToRecent(item: CommandPaletteItem): void {
    // Remove if already exists
    this.recentItems = this.recentItems.filter(r => r.id !== item.id);

    // Add to front
    this.recentItems.unshift({
      ...item,
      type: 'recent'
    });

    // Keep only MAX_RECENT
    this.recentItems = this.recentItems.slice(0, this.MAX_RECENT);

    // Save to localStorage
    this.saveRecentItems();
  }

  private loadRecentItems(): void {
    try {
      const stored = localStorage.getItem('ai-assist-recent-commands');
      if (stored) {
        this.recentItems = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to load recent items:', error);
      this.recentItems = [];
    }
  }

  private saveRecentItems(): void {
    try {
      localStorage.setItem(
        'ai-assist-recent-commands',
        JSON.stringify(this.recentItems)
      );
    } catch (error) {
      console.error('Failed to save recent items:', error);
    }
  }

  // ============================================================
  // HELPERS
  // ============================================================

  private getCommonActions(): ActionDefinition[] {
    const knowledge = this.knowledgeService.getKnowledgeValue();
    if (!knowledge) return [];

    return (Object.values(knowledge.actions) as ActionDefinition[])
      .filter((a: ActionDefinition) =>
        ['navigation', 'export', 'import'].includes(a.category)
      );
  }

  private getIconForRoute(path: string): string {
    const icons: Record<string, string> = {
      '/dashboard': '📊',
      '/contacts': '👥',
      '/deals': '💰',
      '/conversations': '💬',
      '/reports': '📈',
      '/settings': '⚙️',
      '/metrics': '📉',
      '/calendar': '📅',
      '/tasks': '✓',
      '/to-do': '✓'
    };

    return icons[path] || '📄';
  }

  private getIconForCategory(category: string): string {
    const icons: Record<string, string> = {
      navigation: '🧭',
      data: '💾',
      messaging: '📧',
      export: '📤',
      import: '📥',
      settings: '⚙️'
    };

    return icons[category] || '⚡';
  }

  private capitalizeCategory(category: string): string {
    return category.charAt(0).toUpperCase() + category.slice(1);
  }

  // ============================================================
  // TEMPLATE HELPERS
  // ============================================================

  trackByItemId(index: number, item: CommandPaletteItem): string {
    return item.id;
  }
}
